import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { OrganizationTier, type OrgRecord } from "@/shared/domain/orgs";
import type { PaginatedResponse } from "@/shared/http";

import { orgQueryKeys } from "./orgQueryKeys";

export interface ListOrgsParams {
  search?: string;
  tier?: OrganizationTier;
}

export interface ListOrgsResult {
  data: OrgRecord[] | undefined;
  totalCount: number | undefined;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
}

/**
 * Page-aware list of orgs backed by `/v1/orgs/search`. Mirrors the
 * `usePaginatedQueryResults` pattern: an `useInfiniteQuery` chained by
 * `next_token` so each page is fetched at most once, exposing the
 * `currentPage` slice and prefetching the next page in the background.
 */
export function useListOrgs(
  params: ListOrgsParams,
  currentPage: number,
  pageSize: number,
): ListOrgsResult {
  const { orgService } = useDomainServices();

  const q = useInfiniteQuery({
    queryKey: orgQueryKeys.list(params, pageSize),
    queryFn: ({ pageParam, signal }) =>
      orgService.listOrgs(
        {
          search: params.search,
          tier: params.tier,
          pageToken: pageParam,
          limit: pageSize,
        },
        { abortSignal: signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: PaginatedResponse<OrgRecord>) =>
      last.next_token ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  React.useEffect(
    function prefetchNextPage() {
      if (!q.data || q.isFetchingNextPage || q.hasNextPage === false) {
        return;
      }
      const loaded = q.data.pages.length;
      if (currentPage >= loaded - 1) {
        void q.fetchNextPage();
      }
    },
    [currentPage, q.data, q.hasNextPage, q.isFetchingNextPage, q],
  );

  const loaded = q.data?.pages.length;
  const hasNextPage =
    (loaded !== undefined && currentPage + 1 < loaded) || q.hasNextPage;

  // `total_count` is the same across pages of a single query — read it off
  // the first page so the count stays stable while the user paginates.
  const totalCount = q.data?.pages[0]?.total_count ?? undefined;

  return {
    data: q.data?.pages[currentPage]?.items,
    totalCount: totalCount === null ? undefined : totalCount,
    error: q.error,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    hasNextPage,
  };
}
